import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {of, Subject} from 'rxjs';
import {catchError, take, takeUntil} from 'rxjs/operators';
import {ContactsApiService} from '@features/contacts/store/contacts-api.service';
import {chatFaceStyle} from '../../messenger/chat-face';
import {ChatMember, toChatMembers} from '../../messenger/chat-members';
import {addCandidates, memberRowAction, MemberRowAction} from '../../messenger/group-members';
import {
  addRoleAction, addRoleOptions, ADMIN, isOnlyAdmin, MemberRole, roleAction, roleLabel, roleOptions
} from '../../messenger/member-roles';
import {MessengerFlags} from '../../messenger/messenger-flags';
import {MemberStatus} from '../../messenger/messenger.models';
import {MessengerPresencePort} from '../../messenger/presence.port';

/** A member's new role, chosen on their row (ITP-E-204-07). */
export interface GroupPaneRoleChange {
  row: GroupPaneRow;
  permissionType: MemberRole;
}

/** A colleague added from the Add pane, with the role chosen for them (ITP-E-204-07 Part B). */
export interface GroupPaneAdd {
  member: ChatMember;
  permissionType: MemberRole;
}

/** One item of a member row's ⋯ menu: a role to give them, Remove, or (your own row) Leave. */
export type GroupPaneRowItem = 'role-290' | 'role-280' | 'role-270' | 'remove' | 'leave';

/** A row on either pane: a member of the group (with their role in it), or a colleague who could be added. */
export interface GroupPaneRow {
  email: string;
  name: string;
  /** The conversation role's user, handed back as it is to remove them. */
  user?: any;
  permissionType?: number;
  member?: ChatMember;
}

/**
 * THE GROUP'S MEMBERS | ADD PANES (ITP-E-203-05): carbon's membership view, swapped in over the thread on the Chat
 * panel. Who is in the group and who could be, each row with one control: remove and add act at once, the reader's own
 * row leaves. Without permission to manage, the panes are a read-only list with no Add. The host owns the commands.
 *
 * Each member's role shows on their row as plain text, and each row has one ⋯ menu (ITP-E-204-07, Q13): the roles the
 * reader may give that member (messenger/member-roles.ts) and Remove; on the reader's own row, Leave. The members
 * dialog is reachable only from the Messages page, so this is where the panel makes someone admin. Adding someone asks
 * which role they join as. Role changes and Remove act at once; Leave asks its own question, and adding still asks
 * about the history.
 */
@Component({
  standalone: false,
  selector: 'chat-group-members-panes',
  templateUrl: './group-members-panes.component.html',
  styleUrls: ['./group-members-panes.component.scss']
})
export class GroupMembersPanesComponent implements OnInit, OnChanges, OnDestroy {
  /** The open group conversation, as the store holds it. */
  @Input() conversation: any;
  @Input() me: string | null = null;
  /** The reader's permission in the group (`ChatPermissions`, 0 for a participant). */
  @Input() maxPermission = 0;
  @Output() back = new EventEmitter<void>();
  @Output() removeMember = new EventEmitter<GroupPaneRow>();
  @Output() addMember = new EventEmitter<GroupPaneAdd>();
  @Output() leave = new EventEmitter<void>();
  @Output() changeRole = new EventEmitter<GroupPaneRoleChange>();

  readonly faceStyle = chatFaceStyle(44);
  readonly groupFaceStyle = chatFaceStyle(24);
  readonly groupFaceRingStyle = chatFaceStyle(24, true);
  readonly showPresence: boolean;
  pane: 'in' | 'add' = 'in';
  members: GroupPaneRow[] = [];
  candidates: GroupPaneRow[] = [];
  /** The header's two faces: other members, kept while the membership stays the same. */
  faces: GroupPaneRow[] = [];
  address = '';
  presence: Record<string, MemberStatus> = {};

  private people: ChatMember[] = [];
  private readonly unsubscribe$ = new Subject<void>();

  constructor(private contactsApi: ContactsApiService,
              private presencePort: MessengerPresencePort,
              messengerFlags: MessengerFlags) {
    this.showPresence = messengerFlags.isOn('B-15');
  }

  /** Adding and removing need a manager's permission (the dialog's rule, `maxPermission > 0`). */
  get canManage(): boolean {
    return this.maxPermission > 0;
  }

  ngOnInit() {
    this.contactsApi.getAllSharedContacts().pipe(
      take(1),
      catchError(() => of(null)),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      this.people = toChatMembers(response);
      this.refresh();
      this.readPresence();
    });
  }

  ngOnChanges() {
    if (!this.canManage) {
      this.pane = 'in';
    }
    this.refresh();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get rows(): GroupPaneRow[] {
    return this.pane === 'in' ? this.members : this.candidates;
  }

  /**
   * A member row's ⋯ menu: the roles the reader may give them, then Remove for one the reader outranks or matches (a
   * manager, 280, removes participants, not the group's admin, as the members dialog allows); Leave on the reader's own
   * row. Empty when the reader may do nothing with the row, which then shows no ⋯.
   */
  rowItemsOf(row: GroupPaneRow): GroupPaneRowItem[] {
    const action: MemberRowAction = memberRowAction(row, this.me,
      this.canManage && (row.permissionType || 0) <= this.maxPermission);
    if (action === 'leave') {
      return ['leave'];
    }
    const roles: GroupPaneRowItem[] = this.roleOptionsOf(row).map(role => `role-${role}` as GroupPaneRowItem);
    return action === 'remove' ? [...roles, 'remove'] : roles;
  }

  rowItemLabel(item: GroupPaneRowItem): string {
    switch (item) {
      case 'remove':
        return 'Remove';
      case 'leave':
        return 'Leave';
      default:
        return roleAction(Number(item.slice('role-'.length)) as MemberRole);
    }
  }

  rowMenuLabelOf(row: GroupPaneRow): string {
    return `Actions for ${row.name}`;
  }

  onRowItem(row: GroupPaneRow, item: GroupPaneRowItem) {
    switch (item) {
      case 'leave':
        this.leave.emit();
        break;
      case 'remove':
        this.removeMember.emit(row);
        break;
      default:
        this.changeRole.emit({row, permissionType: Number(item.slice('role-'.length)) as MemberRole});
    }
  }

  /** The roles someone added may join as: at or below the reader's own, the everyday one first. */
  get addRoles(): MemberRole[] {
    return addRoleOptions(this.maxPermission);
  }

  addRoleLabel(role: MemberRole): string {
    return addRoleAction(role);
  }

  onAddAs(row: GroupPaneRow, permissionType: MemberRole) {
    this.addMember.emit({member: row.member, permissionType});
  }

  roleLabelOf(row: GroupPaneRow): string {
    return roleLabel(row.permissionType);
  }

  /** The roles the reader may give this member: none on the Add pane, their own row, or one above them. */
  private roleOptionsOf(row: GroupPaneRow): MemberRole[] {
    if (this.pane !== 'in') {
      return [];
    }
    return roleOptions({
      me: {email: this.me, maxPermission: this.maxPermission},
      member: {email: row.email, permissionType: row.permissionType || 0},
      adminCount: this.adminCount
    });
  }

  isOnlyAdmin(row: GroupPaneRow): boolean {
    return isOnlyAdmin({permissionType: row.permissionType || 0}, this.adminCount);
  }

  statusOf(row: GroupPaneRow): MemberStatus | null {
    return this.showPresence ? this.presence[(row.email || '').toLowerCase()] || null : null;
  }

  trackByEmail(index: number, row: GroupPaneRow): string {
    return row.email;
  }

  private get adminCount(): number {
    return this.members.filter(row => row.permissionType === ADMIN).length;
  }

  private refresh() {
    const me = (this.me || '').toLowerCase();
    const roles = (this.conversation?.roles || []).filter(role => !role.hasLeft && role.user?.email);
    const nameOf = (user: any) => [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
    this.members = roles.map(role => ({
      email: role.user.email, name: nameOf(role.user), user: role.user, permissionType: role.permissionType
    }));
    const others = this.members.filter(row => row.email.toLowerCase() !== me);
    if (others.slice(0, 2).map(row => row.email).join() !== this.faces.map(row => row.email).join()) {
      this.faces = others.slice(0, 2);
    }
    this.address = others.map(row => row.name).join(', ');
    this.candidates = addCandidates(this.people, this.members, this.me)
      .map(person => ({email: person.email, name: person.name, member: person}));
  }

  /** B-15: the dots, for members and candidates alike. */
  private readPresence() {
    if (!this.showPresence || !this.people.length) {
      return;
    }
    this.presencePort.statuses(this.people.map(person => ({email: person.email, companyId: person.companyId}))).pipe(
      take(1),
      catchError(() => of({} as Record<string, MemberStatus>)),
      takeUntil(this.unsubscribe$)
    ).subscribe(statuses => this.presence = statuses || {});
  }
}
